import { Box, Fade, Stack } from '@mui/material';
import { raiseUnreachable, validateEnum, validateNotNullOrUndefined } from '@weft/utils';
import { Helmet } from 'react-helmet';
import { withPageLayoutTabs } from '../../components/new-design/layout/VyPageLayout';
import { withGlobalSearchParams } from '../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../components/utility/SuspenseBoundary';
import { useHomePage } from '../../hooks/requests/pages/use-home-page';
import { EmptyPage } from '../empty/EmptyPage';
import type { HomePageTabOption } from './home-page-consts/home-page-tabs';
import { HOME_PAGE_TAB_OPTIONS } from './home-page-consts/home-page-tabs';
import { useHomePageTabs } from './home-page-hooks/use-home-page-tabs';
import { HomePageDataDashboardTabContent } from './home-page-tabs/HomePageDataDashboardTabContent';
import { HomePageOverviewTab } from './home-page-tabs/HomePageOverviewTabContent';
import { HomePageSidebar } from './new-home-page-components/home-page-sidebar/HomePageSidebar';

const HomePage: React.FC = () => {
  const [homePageTabs, currentTab] = useHomePageTabs();
  useHomePage();

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>

      <Stack
        direction="row"
        justifyContent="space-between"
        overflow="hidden"
        sx={{
          height: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            padding: 5,
          }}
        >          
          {homePageTabs.map(({ key }) => {
            validateNotNullOrUndefined(key);
            validateEnum(key, HOME_PAGE_TAB_OPTIONS);

            return (
              (
                <Fade
                  key={key}
                  hidden={currentTab !== key}
                  in={currentTab === key}
                  style={{
                    height: currentTab === key ? 'auto' : 0,
                  }}
                >
                  <Stack
                    gap={2}
                    maxWidth="1200px"
                    overflow="hidden"
                    marginX="auto"
                  >
                    {getTabToRender(key)}
                  </Stack>
                </Fade>
              )
            );
          })}
        </Box>
        <HomePageSidebar/>
      </Stack>
    </>
  );
};

function getTabToRender(tabKey: HomePageTabOption) {
  switch (tabKey) {
    case HOME_PAGE_TAB_OPTIONS.Overview:
      return <HomePageOverviewTab />;
    case HOME_PAGE_TAB_OPTIONS.DataDashboard:
      return <HomePageDataDashboardTabContent />;
    default:
      return raiseUnreachable(tabKey);
  }
}

const HomePageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(withPageLayoutTabs(HomePage, {
  title: 'Home',
  tabsProps: {
    centered: false,
  },
  sx: {
    padding: 0,
    overflow: 'hidden',
  },
}, useHomePageTabs)), {
  errorBoundaryProps: {
    fallback: <EmptyPage />,
  },
});

export { HomePageWithSuspenseBoundary as HomePage };